
import { defineComponent, PropType } from 'vue'
import ExportRunAsMarkdownQuery from '~/apollo/queries/repository/runs/run/exportAsMarkdown.gql'
import { CheckStatus, ScaCheckStatus } from '~/types/types'
import type { GraphqlQueryResponse } from '~/types/apollo-graphql-types'
import { checkStatusBadgeIcon, checkStatusTagLabel } from '~/utils/ui'

/**
 * Nav header component for run details page. Lists:
 *
 * - Information about run like branch name, PR, commits, time taken, time created and other runs on the branch.
 */
export default defineComponent({
  name: 'RunHeader',
  props: {
    title: {
      type: String,
      required: true
    },
    status: {
      type: String as PropType<CheckStatus | ScaCheckStatus>,
      required: true
    },
    analyzerShortcode: {
      type: String,
      default: ''
    },
    targetId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      hasCopiedMarkdown: false,
      isCopyingMarkdown: false
    }
  },
  computed: {
    badgeIcon(): string {
      return checkStatusBadgeIcon(this.status)
    },
    pillLabel(): string {
      return checkStatusTagLabel(this.status)
    },
    copyMarkdownIcon(): string {
      if (this.isCopyingMarkdown) return 'spin-loader'
      return this.hasCopiedMarkdown ? 'check' : 'square-m'
    },
    copyMarkdownLabel(): string {
      if (this.isCopyingMarkdown) return 'Copying...'
      return this.hasCopiedMarkdown ? 'Copied!' : 'Copy as Markdown'
    }
  },
  methods: {
    async copyAsMarkdown(): Promise<void> {
      if (this.isCopyingMarkdown) return
      this.isCopyingMarkdown = true
      try {
        const response = (await this.$fetchGraphqlData(ExportRunAsMarkdownQuery, {
          runId: this.$route.params.runId,
          analyzerShortcode: this.analyzerShortcode,
          targetId: this.targetId
        })) as GraphqlQueryResponse
        const markdown = response.data.exportRunAsMarkdown
        if (markdown) {
          await this.$copyToClipboard(markdown)
          this.hasCopiedMarkdown = true
          setTimeout(() => {
            this.hasCopiedMarkdown = false
          }, 1000)
        }
      } catch (e) {
        this.$toast.danger('Failed to copy run as markdown.')
      } finally {
        this.isCopyingMarkdown = false
      }
    }
  }
})
